/**
 * AI Observability — one traced call.
 *
 * Read top to bottom the way someone debugging asks: did it fail, and where;
 * what did each stage do on the way; what knowledge base was behind it and how
 * was that built; and what did the model actually see and say.
 *
 * The knowledge-base panel shows the chunking AS IT WAS WHEN THE INDEX WAS
 * BUILT — taken from the pipeline run's snapshot, not the pipeline as it stands.
 * "Is it the chunking" is the question that panel exists for, and answering it
 * with settings edited after the fact would point at chunks that never existed.
 */
import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';

import { aiObservabilityService } from '../../services/ai-observability.service';
import {
  KIND_COLOR, MODE_LABEL, OBS_CAPTURE_INFO, fmtCost, fmtMs, fmtTokens, kindLabel, reasoningNote, stepLabel,
  type ObsKbLineage, type ObsTraceBrief, type ObsTraceDetail,
} from '../../types/ai-observability.types';
import { CHIP_FAIL, ICONS, fmtWhen, useObsQuery } from './obs-kit';
import { BackLink, CardHead, ErrorCard } from './obs-shell';
import { Layers } from './obs-layers';
import { Waterfall } from './obs-waterfall';

import '../error-analytics/error-analytics.css';
import './ai-observability.css';

/** A label over a value, for the facts column and the KB grid. */
function Fact({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--obs-ink-4)', marginBottom: 2 }}>{label}</div>
      <div className={mono ? 'obs-mono' : undefined} style={{
        fontSize: 12.5, fontWeight: 500, fontVariantNumeric: 'tabular-nums',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }} title={typeof value === 'string' ? value : undefined}>{value ?? '—'}</div>
    </div>
  );
}

/** One titled group inside the knowledge-base panel. */
function KbGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--obs-ink-3)',
        textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
        {children}
      </div>
    </div>
  );
}

const onOff = (v: unknown) => (v === true ? 'on' : v === false ? 'off' : '—');

/** The knowledge base behind the retrieval, grouped by pipeline stage. */
function KnowledgeBase({ kb }: { kb: ObsKbLineage }) {
  const c = kb.chunking;
  const r = kb.retrieval;
  return (
    <section className="ea-card pad ea-c12" style={{ minWidth: 0 }}>
      <CardHead icon={ICONS.book} title="Knowledge base"
        sub="How the index this answer searched was built, and how it was searched. Chunking is from the pipeline run that built the index, not the pipeline's current settings." />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 22, marginTop: 6 }}>
        <KbGroup title="Chunking">
          <Fact label="Strategy" value={c?.strategy ?? '—'} />
          <Fact label="Chunk size" value={c?.chunkSize != null ? `${c.chunkSize} chars` : '—'} />
          <Fact label="Overlap" value={c?.chunkOverlap != null ? `${c.chunkOverlap} chars` : '—'} />
          <Fact label="Chunks built" value={kb.store?.chunkCount ?? kb.pipeline?.chunksCreated} />
        </KbGroup>
        <KbGroup title="Embedding & store">
          <Fact label="Model" value={kb.embedding?.model} />
          <Fact label="Dimensions" value={kb.embedding?.dimensions} />
          <Fact label="Store" value={kb.store?.provider} mono />
          <Fact label="Index" value={kb.store?.index} mono />
        </KbGroup>
        {/* Only a search has retrieval settings — an ingestion run builds the
            index and searches nothing, so the group would be all dashes. */}
        {r && (
          <KbGroup title="Retrieval settings">
            <Fact label="Type" value={r.type} />
            <Fact label="Top K" value={r.topK} />
            <Fact label="Minimum score" value={r.minimumScore} />
            <Fact label="Max chunks" value={r.maxContextChunks} />
            <Fact label="Token budget" value={r.maxContextTokens} />
            <Fact label="Per-source cap" value={r.maxPerSource ?? 'none'} />
            <Fact label="Reranking" value={onOff(r.reranking)} />
          </KbGroup>
        )}
        <KbGroup title="Source & build">
          <Fact label="Pipeline" value={kb.pipeline?.code} mono />
          <Fact label="Artifact" value={kb.store?.artifactCode} mono />
          <Fact label="Sources" value={kb.store?.sourceCount} />
          <Fact label="Sources failed" value={kb.pipeline?.sourcesFailed} />
          <Fact label="Parser tables" value={onOff(kb.parser?.extractTables)} />
          <Fact label="Whitespace" value={kb.cleaning?.normalizeWhitespace ? 'normalised' : 'kept'} />
        </KbGroup>
      </div>
    </section>
  );
}

/**
 * What the agent was offered against what it called.
 *
 * The whole question of an agent call on one screen: a tool offered and never
 * used, a tool called that was never offered, one called over and over. Read
 * off the steps — `select_tools` knows the offer, the row knows the calls.
 */
function AgentTools({ t }: { t: ObsTraceDetail }) {
  const offered = (t.steps.find((s) => s.name === 'select_tools')?.attrs?.offered as string[]) ?? [];
  const called = new Map((t.tools ?? []).map((x) => [x.name, x.calls]));
  const invented = [...called.keys()].filter((n) => !offered.includes(n));
  const chip = (bg: string, border: string, color: string) => ({ background: bg, borderColor: border, color });
  return (
    <section className="ea-card pad ea-c12" style={{ minWidth: 0 }}>
      <CardHead icon={ICONS.layers} title="Tools"
        sub="What the agent was offered, and what it actually called. Nothing was executed — this is the model's choice alone." />
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 4 }}>
        {offered.map((name) => (
          <span key={name} className="obs-chip" style={called.has(name)
            ? chip('#F0FDF4', '#BBF7D0', '#166534')
            : { color: 'var(--obs-ink-4)' }}
            title={called.has(name) ? `Called ${called.get(name)} time(s)` : 'Offered, never called'}>
            {name}{called.has(name) ? ` ×${called.get(name)}` : ' · unused'}
          </span>
        ))}
        {invented.map((name) => (
          <span key={name} className="obs-chip" style={chip('#FEF2F2', '#FECACA', '#991B1B')}
            title="Called, but never offered">
            {name} · not offered
          </span>
        ))}
        {!offered.length && !invented.length && <span className="ea-sub">No tools were recorded.</span>}
      </div>
    </section>
  );
}

/** A row in the children table — one call made inside this one. */
function ChildRow({ c }: { c: ObsTraceBrief }) {
  const failed = c.status === 'FAILED';
  return (
    <tr>
      <td className="obs-mono" style={{ whiteSpace: 'nowrap' }}>{fmtWhen(c.startedAt, 'clock')}</td>
      <td>
        <span className="obs-chip" style={{ color: KIND_COLOR[c.kind ?? ''],
          borderColor: 'var(--obs-line)', fontWeight: 600 }}>{kindLabel(c.kind)}</span>
      </td>
      <td>
        <Link to={`/ai-observability/tracing/${c.observabilityId}`} style={{
          color: 'var(--obs-ink)', textDecoration: 'none', fontWeight: 500,
          display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' }} title={c.name ?? undefined}>{c.name ?? '—'}</Link>
      </td>
      <td>
        {failed
          ? <span className="obs-chip" style={CHIP_FAIL}>failed at {stepLabel(c.failedStep)}</span>
          : c.warningCount
            ? <span className="obs-chip" style={{ background: '#FEF3C7', borderColor: '#FDE68A',
              color: '#92400E' }}>{c.warningCount} warning{c.warningCount === 1 ? '' : 's'}</span>
            : <span className="obs-chip">ok</span>}
      </td>
      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{c.modelId ?? '—'}</td>
      <td className="num">{fmtMs(c.latencyMs)}</td>
      <td className="num">{fmtCost(c.cost, c.currency)}</td>
    </tr>
  );
}

/** Everything this operation did — an eval run's cases, judges and RAG queries. */
function Children({ items }: { items: ObsTraceBrief[] }) {
  const failed = items.filter((c) => c.status === 'FAILED').length;
  return (
    <section className="ea-card ea-c12" style={{ minWidth: 0, overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 0' }}>
        <CardHead icon={ICONS.table} title={`Inside this run · ${items.length}`}
          sub={`Every AI call this run made, in the order it started${failed ? ` — ${failed} failed` : ''}. Open one for its own waterfall.`} />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="obs-tbl">
          <thead>
            <tr>
              <th>Started</th><th>Kind</th><th>What ran</th><th>Outcome</th>
              <th>Model</th><th className="num">Latency</th><th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>{items.map((c) => <ChildRow key={c.observabilityId} c={c} />)}</tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * ``observabilityId`` lets this render inside a dialog (e.g. the Prompt view's
 * Observability panel) instead of only as a route page; when given, it wins
 * over the URL param and the "back to Calls" link is dropped since there is no
 * page to go back to.
 */
export default function ObsTracingDetail(
  { observabilityId, embedded }: Readonly<{ observabilityId?: string; embedded?: boolean }> = {},
) {
  const { id: routeId = '' } = useParams();
  const id = observabilityId ?? routeId;
  const fetchTrace = useCallback(() => aiObservabilityService.getTracingDetail(id), [id]);
  const { data: t, loading, error, reload } = useObsQuery(fetchTrace, 'Could not load this trace.');

  const failed = t?.status === 'FAILED';
  const failedStep = t?.steps.find((s) => s.status === 'FAILED');
  const usage = t?.usage ?? {};

  return (
    <div className="ea-root obs" style={{ padding: embedded ? 0 : 20, minHeight: embedded ? 0 : '100%' }}>
      <div style={{ marginBottom: 14 }}>
        {!embedded && <BackLink to="/ai-observability/tracing">Calls</BackLink>}
        <h1 style={{ margin: '6px 0 0', fontSize: 19, fontWeight: 600, letterSpacing: '-.015em' }}>
          {t?.name ?? 'Trace'}
        </h1>
        {t && (
          <div className="ea-sub" style={{ marginTop: 5, display: 'flex', gap: 8,
            flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="obs-chip" style={{ color: KIND_COLOR[t.kind],
              borderColor: 'var(--obs-line)', fontWeight: 600 }}>{kindLabel(t.kind)}</span>
            {/* A mode that says nothing (unrecorded, n/a) is noise beside a
                status — most often on a call that failed before the model
                answered, where there was no call shape to record. */}
            {t.mode && t.mode !== 'UNKNOWN' && t.mode !== 'NOT_APPLICABLE' && (
              <span className="obs-chip">{MODE_LABEL[t.mode] ?? t.mode}</span>
            )}
            <span className="obs-chip" style={failed ? CHIP_FAIL : undefined}>
              {failed ? `failed at ${stepLabel(t.failedStep)}` : (t.status ?? 'unrecorded')}
            </span>
            {t.warningCount > 0 && (
              <span className="obs-chip" style={{ background: '#FEF3C7', borderColor: '#FDE68A',
                color: '#92400E' }}>{t.warningCount} warning{t.warningCount === 1 ? '' : 's'}</span>
            )}
            <span>{fmtWhen(t.startedAt, 'full')}</span>
          </div>
        )}
        {t?.parent && (
          <div className="ea-sub" style={{ marginTop: 8, fontSize: 12.5 }}>
            {t.parent.pending
              ? 'Part of a run that is still in progress.'
              : <>Part of {kindLabel(t.parent.kind).toLowerCase()}{' '}
                <Link to={`/ai-observability/tracing/${t.parent.observabilityId}`}
                  style={{ fontWeight: 500 }}>{t.parent.name ?? 'this run'}</Link>
                {t.parent.status === 'FAILED' ? ' (which failed)' : ''}</>}
          </div>
        )}
      </div>

      {loading && <section className="ea-card pad"><div className="obs-empty">Loading…</div></section>}
      <ErrorCard error={error} />

      {t && (
        <div className="ea-grid">
          {failed && (
            <section className="ea-card pad ea-c12" style={{ minWidth: 0, borderColor: '#FECACA' }}>
              <CardHead icon={ICONS.eval} title="Why it failed"
                sub={failedStep
                  ? `It stopped at "${stepLabel(failedStep.name)}" — every stage before it completed.`
                  : 'Recorded before step capture, so the stage it stopped in is unknown.'} />
              <div style={{ fontSize: 13, lineHeight: 1.6, padding: '10px 12px', borderRadius: 6,
                background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }}>
                {failedStep?.error?.message ?? t.errorMessage ?? 'No error message was recorded.'}
              </div>
            </section>
          )}

          <section className="ea-card pad ea-c8" style={{ minWidth: 0 }}>
            <CardHead icon={ICONS.latency} title="Where it went"
              sub="Every stage in order, to scale. Failures and warnings open themselves; click any stage for what it found." />
            {t.steps.length
              ? <Waterfall steps={t.steps} />
              : <div className="obs-empty">
                {t.notCaptured?.includes('steps')
                  ? 'Stage steps are switched off for this type in the Observability Master.'
                  : t.kind === 'EVAL'
                    ? 'An eval run calls no model itself — its work is the calls listed below.'
                    : 'This call was recorded before step capture existed, so its stages were not kept.'}
              </div>}
          </section>

          <section className="ea-card pad ea-c4" style={{ minWidth: 0 }}>
            <CardHead icon={ICONS.model} title="Facts" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px' }}>
              <Fact label="Model" value={t.modelId} />
              <Fact label="Provider" value={t.providerType} />
              <Fact label="Latency" value={fmtMs(t.latencyMs)} />
              <Fact label="Cost" value={fmtCost(t.cost, t.currency)} />
              <Fact label="Input" value={fmtTokens(usage.inputTokens)} />
              <Fact label="Output" value={fmtTokens(usage.outputTokens)} />
              <Fact label="Thinking" value={fmtTokens(usage.reasoningTokens)} />
              <Fact label="Stages" value={t.steps.filter((s) => s.status !== 'WARN').length || '—'} />
            </div>
            {!!t.notCaptured?.length && (
              <div className="ea-sub" style={{ marginTop: 12, fontSize: 12 }}>
                Not captured: {t.notCaptured.map((c) => OBS_CAPTURE_INFO[c]?.label ?? c).join(', ')}
                {' '}— switched off in the <Link to="/ai-observability/master">Observability Master</Link>.
              </div>
            )}
            {t.reasoningState && t.reasoningState !== 'captured' && (
              <div className="ea-sub" style={{ marginTop: 12, fontSize: 12 }}>
                {reasoningNote(t.reasoningState, usage.reasoningTokens)}
              </div>
            )}
          </section>

          {t.layers && <Layers id={t.observabilityId} data={t.layers} onChange={reload} />}
          {t.kind === 'AGENT' && <AgentTools t={t} />}
          {!!t.children.length && <Children items={t.children} />}
          {t.kb && <KnowledgeBase kb={t.kb} />}
          {t.contentHidden && t.kind === 'RAG' && (
            <section className="ea-card pad ea-c12" style={{ minWidth: 0 }}>
              <CardHead icon={ICONS.book} title="Content hidden"
                sub="Prompt & answer text is switched off for this type in the Observability Master. Stages, scores, sources and errors are still shown above." />
              <Link to="/ai-observability/master" className="obs-chip" style={{ textDecoration: 'none' }}>
                Observability Master →
              </Link>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
